import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_SALE = "#5C74C7";
const COLOR_EXCHANGE = "#5CC7C1";
const COLOR_REFUND = "#4E8EF0";

/** Grouped bars still reserve a slot per dataset; omit all-zero series so bars stay centered on the category. */
function seriesHasAnyNonZero(values) {
  if (!Array.isArray(values)) return false;
  return values.some((v) => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n !== 0;
  });
}

function formatAmountAxis(value) {
  const v = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

/** @param {unknown} n */
function formatTableNumber(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.round(v));
}

/**
 * “By Amounts” / “By Qty” breakdown — custom listbox (Figma dropdown).
 * @param {{ value: 'amount' | 'quantity'; onChange?: (v: 'amount' | 'quantity') => void }} props
 */
export function TotalSaleGroupByDropdown({ value, onChange }) {
  const { t } = useTranslation();
  const menuId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const labelAmount = t("By Amounts", { defaultValue: "By Amounts" });
  const labelQty = t("By Qty", { defaultValue: "By Qty" });
  const triggerLabel = value === "quantity" ? labelQty : labelAmount;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const pick = (next) => {
    if (next === "amount" || next === "quantity") onChange?.(next);
    close();
  };

  return (
    <div className="pos-dashboard-chart-card__groupby" ref={wrapRef}>
      <button
        type="button"
        className="pos-dashboard-chart-card__groupby-trigger"
        aria-label={t("Breakdown", { defaultValue: "Breakdown" })}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pos-dashboard-chart-card__groupby-trigger-text">{triggerLabel}</span>
        <span className="pos-dashboard-chart-card__groupby-chevron" aria-hidden>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="#8A8A8A" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open ? (
        <div id={menuId} className="pos-dashboard-chart-card__groupby-menu" role="listbox" aria-label={t("Breakdown", { defaultValue: "Breakdown" })}>
          <div className="pos-dashboard-chart-card__groupby-menu-inner">
            <button
              type="button"
              role="option"
              aria-selected={value === "amount"}
              className={`pos-dashboard-chart-card__groupby-option ${value === "amount" ? "pos-dashboard-chart-card__groupby-option--selected" : ""}`}
              onClick={() => pick("amount")}
            >
              <span className="pos-dashboard-chart-card__groupby-option-row">{labelAmount}</span>
            </button>
            <button
              type="button"
              role="option"
              aria-selected={value === "quantity"}
              className={`pos-dashboard-chart-card__groupby-option ${value === "quantity" ? "pos-dashboard-chart-card__groupby-option--selected" : ""}`}
              onClick={() => pick("quantity")}
            >
              <span className="pos-dashboard-chart-card__groupby-option-row">{labelQty}</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatQtyAxis(value) {
  const v = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(v)) return "";
  return String(Math.round(v));
}

/**
 * Total Sale data table — sortable headers.
 *
 * @param {{ model: import('./dashboardChartsModel.js').TotalSaleGroupedModel; groupBy: 'amount' | 'quantity' }} props
 */
function TotalSaleDataTable({ model, groupBy }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {'item'|'sale'|'exchange'|'refund'|null} */ (null),
    dir: /** @type {'asc'|'desc'} */ ("asc"),
  }));

  const rows = useMemo(() => {
    const categories = model?.categories ?? [];
    const sale = model?.sale ?? [];
    const exchange = model?.exchange ?? [];
    const refund = model?.refund ?? [];
    const saleQty = model?.saleQty ?? [];
    const exchangeQty = model?.exchangeQty ?? [];
    const refundQty = model?.refundQty ?? [];
    const useQty = groupBy === "quantity";
    const n = Math.max(
      categories.length,
      sale.length,
      exchange.length,
      refund.length,
      useQty ? saleQty.length : 0,
      useQty ? exchangeQty.length : 0,
      useQty ? refundQty.length : 0,
    );
    const out = [];
    for (let i = 0; i < n; i += 1) {
      const sv = useQty ? saleQty[i] : sale[i];
      const ev = useQty ? exchangeQty[i] : exchange[i];
      const rv = useQty ? refundQty[i] : refund[i];
      out.push({
        key: `${String(categories[i] ?? "")}-${String(i)}`,
        item: categories[i] ?? "",
        sale: typeof sv === "number" ? sv : Number(sv) || 0,
        exchange: typeof ev === "number" ? ev : Number(ev) || 0,
        refund: typeof rv === "number" ? rv : Number(rv) || 0,
      });
    }
    return out;
  }, [model, groupBy]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const mul = sort.dir === "asc" ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      let c = 0;
      if (sort.key === "item") c = String(a.item).localeCompare(String(b.item), undefined, { sensitivity: "base" });
      else if (sort.key === "sale") c = a.sale - b.sale;
      else if (sort.key === "exchange") c = a.exchange - b.exchange;
      else if (sort.key === "refund") c = a.refund - b.refund;
      return c * mul;
    });
    return copy;
  }, [rows, sort.key, sort.dir]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  return (
    <div className="pos-dashboard-chart-table" role="table" aria-label={t("Total Sale", { defaultValue: "Total Sale" })}>
      <div className="pos-dashboard-chart-table__thead" role="rowgroup">
        <div className="pos-dashboard-chart-table__tr pos-dashboard-chart-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--first"
            ariaSortLabel={t("Sort by Item", { defaultValue: "Sort by Item" })}
            sorted={sort.key === "item"}
            direction={sort.dir}
            onSort={() => toggleSort("item")}
          >
            {t("Item", { defaultValue: "Item" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Sale", { defaultValue: "Sort by Sale" })}
            sorted={sort.key === "sale"}
            direction={sort.dir}
            onSort={() => toggleSort("sale")}
          >
            {t("Sale", { defaultValue: "Sale" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Exchange", { defaultValue: "Sort by Exchange" })}
            sorted={sort.key === "exchange"}
            direction={sort.dir}
            onSort={() => toggleSort("exchange")}
          >
            {t("Exchange", { defaultValue: "Exchange" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            className="pos-dashboard-chart-table__th--last pos-dashboard-chart-table__th--num"
            ariaSortLabel={t("Sort by Refund", { defaultValue: "Sort by Refund" })}
            sorted={sort.key === "refund"}
            direction={sort.dir}
            onSort={() => toggleSort("refund")}
          >
            {t("Refund", { defaultValue: "Refund" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-dashboard-chart-table__scroll" role="rowgroup">
        {sortedRows.map((row) => (
          <div key={row.key} className="pos-dashboard-chart-table__tr" role="row">
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--label" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{row.item}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--num" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableNumber(row.sale)}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--num" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableNumber(row.exchange)}</span>
            </div>
            <div className="pos-dashboard-chart-table__td pos-dashboard-chart-table__td--amount" role="cell">
              <span className="pos-dashboard-chart-table__cell-inner">{formatTableNumber(row.refund)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('./dashboardChartsModel.js').TotalSaleGroupedModel} props.model
 * @param {(value: string) => void} [props.onGroupByChange]
 * @param {() => void} [props.onMenuChart]
 * @param {() => void} [props.onMenuTable]
 * @param {() => void} [props.onMenuDownload]
 * @param {() => void} [props.onMenuRemove]
 */
function TotalSaleGroupedBarCard({
  model,
  onGroupByChange,
  onMenuChart,
  onMenuTable,
  onMenuDownload,
  onMenuRemove,
}) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState("chart");
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("total-sale"));
    onMenuDownload?.();
  }, [onMenuDownload]);

  const handleViewModeChange = useCallback(
    (mode) => {
      setViewMode(mode);
      if (mode === "chart") onMenuChart?.();
      if (mode === "table") onMenuTable?.();
    },
    [onMenuChart, onMenuTable],
  );

  const groupBy = model?.groupBy ?? "amount";
  const hasCategories = (model?.categories?.length ?? 0) > 0;

  const { chartData, options } = useMemo(() => {
    const categories = model?.categories ?? [];
    const useQty = groupBy === "quantity";
    const saleSeries = useQty ? (model?.saleQty ?? []) : (model?.sale ?? []);
    const exchangeSeries = useQty ? (model?.exchangeQty ?? []) : (model?.exchange ?? []);
    const refundSeries = useQty ? (model?.refundQty ?? []) : (model?.refund ?? []);

    const saleLabel = t("Sale", { defaultValue: "Sale" });
    const exchangeLabel = t("Exchange", { defaultValue: "Exchange" });
    const refundLabel = t("Refund", { defaultValue: "Refund" });

    const datasetCandidates = [
      { label: saleLabel, data: saleSeries, backgroundColor: COLOR_SALE },
      { label: exchangeLabel, data: exchangeSeries, backgroundColor: COLOR_EXCHANGE },
      { label: refundLabel, data: refundSeries, backgroundColor: COLOR_REFUND },
    ];

    const nonZero = datasetCandidates.filter((d) => seriesHasAnyNonZero(d.data));
    const datasetsForChart = nonZero.length ? nonZero : datasetCandidates;

    const data = {
      labels: categories,
      datasets: datasetsForChart.map((d) => ({
        label: d.label,
        data: d.data,
        backgroundColor: d.backgroundColor,
        maxBarThickness: 18,
        borderRadius: 0,
        borderSkipped: false,
      })),
    };

    const yAxisTitle = useQty ? t("Quantity", { defaultValue: "Quantity" }) : t("Amount", { defaultValue: "Amount" });
    const tickFormat = useQty ? (v) => formatQtyAxis(v) : (v) => formatAmountAxis(v);

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 20,
            color: "#000000",
            font: {
              family: "'Segoe UI', Roboto, sans-serif",
              size: 12,
            },
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            title: (items) => {
              const item = items[0];
              if (!item) return [];
              const chart = item.chart;
              const labels = chart?.data?.labels ?? [];
              const raw = labels[item.dataIndex];
              const text = raw != null ? String(raw) : "";
              return text ? [`${text}:`] : [];
            },
            /** One line per metric so the tooltip matches the table even when all-zero series are hidden from the plot. */
            afterTitle: (items) => {
              const item = items[0];
              if (item == null) return [];
              const i = item.dataIndex;
              const row = (lab, arr) => `${lab}: ${formatTableNumber(arr[i])}`;
              return [row(saleLabel, saleSeries), row(exchangeLabel, exchangeSeries), row(refundLabel, refundSeries)];
            },
            label: () => null,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: "#757575",
            font: {
              family: "Roboto",
              size: 11,
            },
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          title: {
            display: true,
            text: yAxisTitle,
            color: "#373D3F",
            font: {
              size: 11,
            },
          },
          ticks: {
            color: "#373D3F",
            font: {
              size: 11,
            },
            callback: (v) => tickFormat(v),
          },
          grid: {
            color: "#E0E0E0",
            drawTicks: false,
          },
          border: {
            display: false,
          },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [model, groupBy, t]);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--total-sale">
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--with-controls">
        <h2 className="pos-dashboard-chart-card__title">{t("Total Sale", { defaultValue: "Total Sale" })}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <TotalSaleGroupByDropdown
            value={groupBy === "quantity" ? "quantity" : "amount"}
            onChange={(v) => onGroupByChange?.(v)}
          />
          <DashboardChartCardKebabMenu
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div className={`pos-dashboard-chart-card__body ${viewMode === "table" ? "pos-dashboard-chart-card__body--table" : ""}`}>
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          {viewMode === "table" ? (
            <TotalSaleDataTable model={model} groupBy={groupBy === "quantity" ? "quantity" : "amount"} />
          ) : hasCategories ? (
            <div className="pos-dashboard-chart-card__canvas">
              <Bar data={chartData} options={options} />
            </div>
          ) : (
            <div className="pos-dashboard-chart-card__empty" role="status">
              {t("No data", { defaultValue: "No data" })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TotalSaleGroupedBarCard;
